import { useQuery } from '@apollo/client';
import { Autocomplete, Box, TextField, type AutocompleteProps } from '@mui/material';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { namedByPlaceholder } from '~/components/forms/autocomplete-label';
import { useAppContext } from '~/contexts';
import { AvailableChannelsDocument } from './ChannelAutocomplete.generated';

export interface ChannelAutocompleteProps
  extends Omit<AutocompleteProps<number, false, false, false>, 'options' | 'renderInput'> {
  error?: boolean;
  helperText?: React.ReactNode;
  name?: string;
}

export const ChannelAutocomplete = ({
  error,
  helperText,
  name,
  ...autocompleteProps
}: ChannelAutocompleteProps) => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();

  const { data, loading } = useQuery(AvailableChannelsDocument, {
    variables: { networkId: currentNetwork.id },
  });

  const channelsById = useMemo(() => {
    return new Map(
      data?.network?.allChannels.map((channel) => [channel.id, channel] as const) ?? [],
    );
  }, [data?.network?.allChannels]);

  const options = useMemo(() => [...channelsById.keys()], [channelsById]);

  if (loading && !data?.network?.allChannels) return null;

  return (
    <Autocomplete<number>
      fullWidth
      getOptionLabel={(option) => {
        const channel = channelsById.get(option);
        return channel ? `${channel.name} ${channel.number ? ` (${channel.number})` : ''}` : '';
      }}
      loading={loading}
      options={options}
      renderInput={(params) => (
        <TextField
          error={error}
          helperText={helperText}
          name={name}
          {...namedByPlaceholder(
            params,
            t('pages.device_signals.settings.placeholders.select_channel'),
          )}
        />
      )}
      renderOption={(props, option) => {
        const channel = channelsById.get(option);
        return (
          <Box component="li" {...props}>
            {channel && (
              <>
                <Box
                  sx={{
                    backgroundColor: channel.backgroundColor,
                    mr: 1,
                    '& > img': { flexShrink: 0 },
                  }}
                >
                  <img
                    loading="lazy"
                    width="20"
                    srcSet={channel.thumbnailFile.uri}
                    src={channel.thumbnailFile.uri}
                    alt=""
                  />
                </Box>
                {channel.name}
                {channel.number ? ` (${channel.number})` : ''}
              </>
            )}
          </Box>
        );
      }}
      {...autocompleteProps}
    />
  );
};
